import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * Collapses concurrent, identical GETs into a single network request.
 *
 * Several pages fire the same request two or three times within a few milliseconds of
 * each other. Measured on a cold load of `/s/us/ko/dividend`:
 *
 *     yield/dividend_details   started 1065ms and 1070ms
 *     yield/sectoravg          started 1065ms and 1071ms
 *     yield/dividend_ts        started 1065ms and 1071ms
 *
 * Identical path, identical query string, five milliseconds apart. Because they overlap,
 * no HTTP cache can collapse them — the backend does the work twice. `/s/…/insiders`
 * behaves the same way, and one endpoint on `/s/…/short-interest` is requested three
 * times.
 *
 * The underlying cause is a component mounting more than once — the effects behind these
 * calls all declare `[]` dependencies, so they should run once per mount. This module
 * does not fix that; it makes the duplicate *requests* free while the remount is chased
 * separately (see the issue). It is deliberately narrow:
 *
 *   * GET only. Nothing else is safe to collapse — two POSTs are two intentions.
 *   * In-flight only. Once a request settles its entry is dropped, so this is not a
 *     response cache and cannot serve anything stale.
 *   * Requests carrying a `signal`, `cancelToken`, custom `headers`, a `responseType` or
 *     progress callbacks opt out, since joining them would let one caller's abort or
 *     configuration affect another's.
 */

const inFlight = new Map<string, Promise<AxiosResponse>>();

/**
 * Whether a request is safe to share with an identical one already in flight.
 */
const isShareable = (config?: AxiosRequestConfig): boolean =>
    !config?.signal &&
    !config?.cancelToken &&
    !config?.headers &&
    !config?.responseType &&
    !config?.onDownloadProgress;

/**
 * Identity of a GET, as the server sees it.
 *
 * Params are merged with `axios.defaults.params` and sorted, so two call sites that build
 * the same query in a different order still match. A trailing slash is not significant to
 * the backend, and two call sites disagreeing about one should not defeat the dedupe.
 */
export const requestKey = (url: string, config?: AxiosRequestConfig): string => {
    const params: Record<string, unknown> = { ...(axios.defaults.params || {}), ...(config?.params || {}) };
    const query = Object.keys(params)
        .filter((k) => params[k] !== undefined)
        .sort()
        .map((k) => `${k}=${String(params[k])}`)
        .join("&");
    const [path, inlineQuery] = url.split("?");
    return `${path.replace(/\/+$/, "")}?${[inlineQuery, query].filter(Boolean).join("&")}`;
};

/**
 * Wrap `axios.get` so concurrent identical requests share one round trip.
 *
 * Call once, at app start. Joiners get a shallow copy of the response envelope so that
 * assigning to `res.status` or `res.headers` in one caller cannot be seen by another;
 * `res.data` is still shared by reference, so callers must continue to treat it as
 * read-only, which is what they already do.
 */
export const installGetDeduplication = (): void => {
    // Typed explicitly: `bind` erases the generic signature, and these calls pass one.
    const original: typeof axios.get = axios.get.bind(axios);

    axios.get = function dedupedGet<T>(url: string, config?: AxiosRequestConfig) {
        if (!isShareable(config)) return original<T>(url, config);

        const key = requestKey(url, config);
        const existing = inFlight.get(key);
        if (existing) return existing.then((res) => ({ ...res })) as Promise<AxiosResponse<T>>;

        const request = original<T>(url, config).finally(() => {
            inFlight.delete(key);
        });
        inFlight.set(key, request as unknown as Promise<AxiosResponse>);
        return request;
    } as typeof axios.get;
};

/** Test seam: how many requests are currently in flight. */
export const inFlightCount = (): number => inFlight.size;
